import React, { useState } from 'react';
import { Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const plans = [
  {
    name: 'Free',
    price: '$0/month',
    subTitle: 'Forever Free – Ideal for individuals',
    features: [
      '1 Node',
      '2 Domains',
      '3 days data retention duration',
      'Limited access for dashboard',
      'Monitoring support',
      'Assisted setup'
    ],
    buttonText: 'Start for Free',
  },
  {
    name: 'Basic',
    price: '$25/month',
    subTitle: 'Ideal for business starting to scale or experimenting',
    features: [
      '5 Nodes',
      '5 Domains',
      '7 days data retention duration',
      'Full dashboard access',
      'Limited monitoring support',
      'Basic setup guidance'
    ],
    buttonText: 'Subscribe now',
  },
  {
    name: 'Standard',
    price: '$169/month',
    subTitle: 'Ideal for large-scale business needing robust monitoring',
    features: [
      '20 Nodes',
      '20 Domains',
      '15 days data retention duration',
      'Full dashboard access with customization',
      '24x7 NOC team for monitoring & escalation*',
      'Complete setup assistance'
    ],
    buttonText: 'Subscribe now',
  },
  {
    name: 'Enterprise',
    price: 'Contact Sales',
    subTitle: 'Need something specific? Let us know your requirements',
    features: [
      'Scalable nodes',
      'Scalable domains',
      'Custom retention option',
      'Full dashboard access with customization**',
      '24x7 NOC team for monitoring & escalation*',
      'Complete setup assistance including customer data exporter'
    ],
    buttonText: 'Contact Sales',
  },
];

const Pricing = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();

  const handleSelectPlan = (planKey) => {
    navigate(`/dashboard/subscription/${planKey}`);
  };

  return (
    <div id="pricing" style={styles.container}>
      <h2 style={styles.heading}>Pricing Plan</h2>
      <Typography component="p" variant="h4" sx={{ color: '#4b5563', mb: 8 }}>
        Choose the plan that suits your needs
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: { xs: 'wrap', md: 'nowrap' }, // xs < 940px, md >= 940px
        }}
      >
        {plans.map((plan, idx) => {
          const isHovered = hoveredIndex === idx;
          const isPopular = plan.name === 'Standard';
          return (
            <a
              key={idx}
              href={`/dashboard/subscription/${plan.name.toLowerCase()}`}
              aria-label={`Select the ${plan.name} plan: ${plan.price}`}
              onClick={(e) => {
                e.preventDefault();
                handleSelectPlan(plan.name.toLowerCase());
              }}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                ...styles.planCard,
                ...(isHovered || isPopular ? styles.featuredCard : {}),
                ...(isPopular ? styles.popularCard : {}),
              }}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {isPopular && (
                <div style={styles.popularBadge}>
                  ⭐ Popular
                </div>
              )}

              <div style={styles.cardContent}>
                <h3 style={styles.planName}>{plan.name}</h3>
                <p style={styles.planPrice}>
                  {plan.price}
                  {(plan.name === 'Standard' || plan.name === 'Basic') && (
                    <sup style={{ fontSize: '0.5em' }}>#</sup>
                  )}
                </p>
                <Typography
                  component="p"
                  variant="subtitle2"
                  align="center"
                  sx={{ my: 2, px: 1, minHeight: '48px', color: '#334155' }}
                >
                  {plan.subTitle}
                </Typography>

                <ul style={styles.featuresList}>
                  {plan.features.map((feature, index) => {
                    const lowerFeature = feature.trim().toLowerCase();
                    const isNegative = lowerFeature.startsWith('monitoring') || lowerFeature.startsWith('assisted');

                    return (
                      <li key={index} style={styles.featureItem}>
                        <span
                          style={{
                            ...styles.iconMark,
                            backgroundColor: isNegative ? '#a1a1a19e' : '#007bff',
                          }}
                        >
                          {isNegative ? '✖' : '✔'}
                        </span>
                        <span style={styles.featureText}>{feature}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div
                style={{
                  ...styles.button,
                  ...(isHovered || isPopular ? styles.featuredButton : {}),
                  pointerEvents: 'none', // makes sure only the card handles clicks
                }}
              >
                {plan.buttonText}
              </div>
            </a>
          );
        })}
      </Box>
      {/* Additional Notes */}
      <Box sx={styles.notesContainer}>
        <Box sx={styles.noteItem}>
          <span style={styles.noteIcon}>*</span>
          <Typography variant="body1" sx={styles.noteText}>
            We have in-house NOC team available 24x7 to help you manage escalation and deal with false alarms.
          </Typography>
        </Box>

        <Box sx={styles.noteItem}>
          <span style={styles.noteIcon}>**</span>
          <Typography variant="body1" sx={styles.noteText}>
            Database, webserver and other popular metrics available.
          </Typography>
        </Box>

        <Box sx={styles.noteItem}>
          <Typography
            variant="caption"
            sx={{ ...styles.noteText, color: '#4b5563', display: 'flex', alignItems: 'center' }}
          >
            <span style={{ fontWeight: 'bold', paddingRight: '4px' }}>#</span>
            Prices do not include tax. Additional charges may be applied at checkout.
          </Typography>
        </Box>
      </Box>

    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f2f8ff',
    textAlign: 'center',
    paddingTop: '50px',
    paddingBottom: '80px',
  },
  heading: {
    fontSize: '2.4rem',
    marginBottom: '40px',
    fontWeight: '700',
    color: '#002244',
  },
  planCard: {
    backgroundColor: '#ffffff',
    color: '#003366',
    borderRadius: '14px',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '20px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
    minHeight: '420px',
  },
  featuredCard: {
    transform: 'scale(1.05)',
    boxShadow: '0 10px 28px rgba(0, 123, 255, 0.3)',
    border: '2px solid #007bff',
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '15px',
  },
  planName: {
    fontSize: '1.3rem',
    fontWeight: '700',
    marginBottom: '8px',
  },
  planPrice: {
    fontSize: '1.5rem',
    fontWeight: '800',
    color: '#0056b3',
    marginBottom: '12px',
  },
  featuresList: {
    listStyleType: 'none',
    padding: 0,
    marginTop: '8px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '240px',   
    overflowY: 'auto',    
    scrollbarWidth: 'thin', 
  },
  featureItem: {
    fontSize: '0.9rem',
    color: '#003366',
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    backgroundColor: '#005fcc',
    color: 'white',
    border: 'none',
    borderRadius: '25px',
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: '600',
    letterSpacing: '0.05em',
    boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)',
    transition: 'all 0.3s ease',
    marginTop: 'auto',
  },
  featuredButton: {
    backgroundColor: '#004080',
    boxShadow: '0 6px 14px rgba(0, 64, 128, 0.4)',
  },
  iconMark: {
    display: 'inline-block',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    textAlign: 'center',
    lineHeight: '18px',
    marginRight: '8px',
    fontWeight: '700',
    fontSize: '0.75rem',
  },
  featureText: {
    flex: 1,
    wordBreak: 'break-word',
  },
  popularCard: {
    position: 'relative',
    borderColor: '#2065D1',
    boxShadow: '0 10px 28px rgba(52, 159, 236, 0.3)',
  },
  popularBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: '#005fcc',
    color: 'white',
    padding: '3px 8px',
    borderRadius: '18px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    zIndex: 1,
  },
  notesContainer: {
    mt: 8,
    textAlign: 'center',
    maxWidth: '900px',
    mx: 'auto',
    px: 2,
  },
  noteItem: {
    display: 'flex',
    alignItems: 'flex-start',
    textAlign: 'left',
    mb: 1.5,
  },
  noteIcon: {
    fontSize: '1.2rem',
    flexShrink: 0,
    marginTop: 1,
    fontWeight: 'bold',
    paddingRight: '4px',
  },

  noteText: {
    fontSize: '1.1rem',
    lineHeight: 1.5,
    textAlign: 'left',
  },
};

export default Pricing;
